import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';
import {mode} from '@chakra-ui/theme-tools';

import {tokens as darkTokens} from '../../../generated/dark';
import {tokens} from '../../../generated/light';
import {inputTheme} from '../../../theme-config/components/Input';
import {labelTheme} from '../../Label/theme';
import {textTheme} from '../../Text/theme';

import {tableFiltersMenuAnatomy} from './anatomy';
import {vars} from '.';

const {semantic, components} = tokens;
const {
  components: {[`context-menu`]: contextMenu},
} = tokens;
const {
  components: {[`context-menu`]: contextMenuDark},
} = darkTokens;
const {definePartsStyle, defineMultiStyleConfig} = createMultiStyleConfigHelpers(tableFiltersMenuAnatomy.keys());

const baseStyle = definePartsStyle(props => ({
  list: {
    [vars.width.variable]: `264px`, // TODO: replace with token
    [vars.height.variable]: `50dvh`,
    width: vars.width.reference,
    minWidth: vars.width.reference,

    maxHeight: vars.height.reference,

    display: `flex`,
    flexGrow: 1,
    flexDirection: `column`,
    overflowY: `auto`,
    overflowX: `hidden`,
    alignSelf: `stretch`,
    justifySelf: `stretch`,

    // Styling
    background: mode(contextMenu[`background-default`], contextMenuDark[`background-default`])(props),
    borderRadius: `radius-150`,
    boxShadow: `xxl`,
    padding: `space-100`,
    position: `relative`,
    gap: semantic.spacing.gutter[`component-default-vertical`],
    scrollbarGutter: `auto | stable && both-edges?`,
    borderWidth: `0`, // Reset border width
  },

  group: {
    // Layout
    display: `flex`,
    flexDirection: `column`,
    alignItems: `flex-start`,
    alignSelf: `start`,

    // Styling
    mx: 0, // Reset margin
    marginTop: `0`,
    marginBottom: `0`,
    p: `space-050`,
    width: `fit-content`,

    color: mode(contextMenu[`text-default`], contextMenuDark[`text-default`])(props),
    ...labelTheme.baseStyle?.(props),
    ...labelTheme.sizes?.sm,
  },

  item: {
    paddingLeft: `space-100`,

    display: `flex`,
    gap: `space-100`, // TODO: fix token on Figma
    alignSelf: `stretch`,
    alignItems: `center`,
    px: `space-050`,
    py: `space-050`,

    // Styling
    borderRadius: `radius-100`,
    backgroundColor: mode(contextMenu[`background-default`], contextMenuDark[`background-default`])(props),
    color: mode(contextMenu[`text-default`], contextMenuDark[`text-default`])(props),
    // Typography
    ...(textTheme.baseStyle?.(props) ?? {}),
    ...textTheme.sizes?.md,

    _hover: {
      backgroundColor: mode(contextMenu[`background-hover`], contextMenuDark[`background-hover`])(props),
    },

    _active: {
      backgroundColor: mode(contextMenu[`background-pressed`], contextMenuDark[`background-pressed`])(props),
      color: mode(contextMenu[`text-active`], contextMenuDark[`text-active`])(props),
    },

    _focus: {
      backgroundColor: mode(contextMenu[`background-selected`], contextMenuDark[`background-selected`])(props),
    },

    _disabled: {
      backgroundColor: mode(contextMenu[`background-disabled`], contextMenuDark[`background-disabled`])(props),
      color: mode(contextMenu[`text-disabled`], contextMenuDark[`text-disabled`])(props),
      cursor: `not-allowed`,
    },

    // Transitions
    transition: `background-color 0.1s ease-in-out`,

    [`&[data-last]`]: {
      borderBottomRadius: `radius-100`,
    },

    [`&[data-first]`]: {
      borderTopRadius: `radius-100`,
    },
  },
  divider: {
    width: `100%`,
    borderColor: semantic.colors.stroke.primary,
    opacity: `1`,
    marginTop: `space-050`,
    marginBottom: `space-050`,
  },
  panelHeader: {
    color: mode(contextMenu[`text-default`], contextMenuDark[`text-default`])(props),
    ...labelTheme.baseStyle?.(props),
    px: `space-050`,
    display: `flex`,
    alignItems: `center`,
    gap: `space-050`,
    ...textTheme.sizes?.sm,
  },
  searchInput: {
    ...inputTheme.baseStyle?.field,
    ...inputTheme.sizes?.lg.field,

    // Consider moving those to inputTheme
    display: `flex`,
    alignItems: `center`,
    gap: `space-050`,
    flex: `1 1 0`,
    py: components[`form-control`][`padding-top-bottom`],
    px: components[`form-control`][`padding-side`],
    background: components[`form-control`][`background-default`],
    boxShadow: `xs`,
    color: semantic.colors.text.primary,
    overflow: `hidden`,
    textOverflow: `ellipsis`,
    whiteSpace: `nowrap`,
    borderRadius: `radius-150`, // TODO: ask dana to fix to radius token
    borderColor: mode(
      components[`form-control`][`stroke-default`],
      components[`form-control`][`stroke-default`],
    )(props),
    width: `100%`,
    _hover: {
      borderColor: components[`form-control`][`stroke-hover`],
    },

    _placeholder: {
      color: mode(
        components[`form-control`][`text-placeholder`],
        components[`form-control`][`text-placeholder`],
      )(props),
    },

    _invalid: {
      borderColor: semantic.colors.stroke.state[`danger-subtle`],
      background: semantic.colors.background.state[`danger-subtle`],
      color: mode(components[`form-control`][`text-error`], components[`form-control`][`text-error`])(props),
    },

    _disabled: {
      background: mode(
        components[`form-control`][`background-disabled`],
        components[`form-control`][`background-disabled`],
      )(props),
      color: mode(components[`form-control`][`text-disabled`], components[`form-control`][`text-disabled`])(props),
      borderColor: mode(
        components[`form-control`][`stroke-disabled`],
        components[`form-control`][`stroke-disabled`],
      )(props),
      cursor: `not-allowed`,
    },

    _focus: {
      borderColor: mode(components[`form-control`][`stroke-focus`], components[`form-control`][`stroke-focus`])(props),
      color: mode(components[`form-control`][`text-default`], components[`form-control`][`text-default`])(props),
      _placeholderShown: {
        color: mode(
          components[`form-control`][`text-placeholder`],
          components[`form-control`][`text-placeholder`],
        )(props),
      },
    },

    _focusVisible: {},
  },
}));

export const filtersTheme = defineMultiStyleConfig({
  baseStyle,
  variants: {},
  sizes: {},
  defaultProps: {},
});
